import { FOLLOW_UP_TYPE_LABELS } from '@chukua/common';
import { Button, Tag } from 'antd';
import type { CollectionFollowUpView } from '../follow-ups.api';
import { formatContactedAt } from '../policies/follow-up-time';

interface FollowUpTimelineProps {
  items: CollectionFollowUpView[];
  /** Absent when the viewer cannot record: a correction is a new entry, so it needs the same permission. */
  onCorrect?: (entry: CollectionFollowUpView) => void;
  /** Absent when the viewer cannot record: a promise made in this follow-up is recorded from here. */
  onRecordPromise?: (entry: CollectionFollowUpView) => void;
}

/** Newest contact first; an entry is evidence and is never edited, so "Correct" opens a new entry that points at it. */
export function FollowUpTimeline({ items, onCorrect, onRecordPromise }: FollowUpTimelineProps) {
  const correctedIds = new Set(items.map((entry) => entry.correctsFollowUpId).filter(Boolean));

  return (
    <ol className="m-0 flex list-none flex-col gap-3 p-0">
      {items.map((entry) => (
        <li key={entry.id} className="rounded-xl border border-line bg-surface p-4">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <Tag className="m-0">{FOLLOW_UP_TYPE_LABELS[entry.type]}</Tag>
            <span className="font-medium text-fg">{formatContactedAt(entry.contactedAt)}</span>
            {entry.invoiceNumber && <span className="font-mono text-muted">{entry.invoiceNumber}</span>}
            {entry.correctsFollowUpId && <Tag className="m-0">Correction</Tag>}
            {correctedIds.has(entry.id) && <Tag className="m-0">Corrected below</Tag>}
            <span className="ml-auto flex gap-1">
              {onRecordPromise && (
                <Button size="small" type="link" onClick={() => onRecordPromise(entry)}>
                  Record promise
                </Button>
              )}
              {onCorrect && (
                <Button size="small" type="link" onClick={() => onCorrect(entry)}>
                  Correct
                </Button>
              )}
            </span>
          </div>
          <p className="mt-2 mb-0 text-fg">{entry.outcome}</p>
          {entry.notes && <p className="mt-1 mb-0 whitespace-pre-wrap text-muted">{entry.notes}</p>}
          <p className="mt-2 mb-0 text-sm text-muted">
            {entry.contactName ? `Spoke to ${entry.contactName} · ` : ''}Recorded by {entry.recordedBy.name}
          </p>
        </li>
      ))}
    </ol>
  );
}
